import 'react-circular-progressbar/dist/styles.css';
import styles from './style.module.css';

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '../../components/Button';
import { ButtonBack } from '../../components/ButtonBack';
import { Card } from '../../components/Card';
import { List, ListItem } from '../../components/List';
import { Notify } from '../../components/Notify';
import { ModalHasAnswered } from './components/ModalHasAnsered';
import { ProgressHorizontal } from './components/ProgressHorizontal';

import { ACTION_DETAILS, HOME } from '../../constants/Routes';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../server/api';
import Skeleton from 'react-loading-skeleton';
import { IAttempt } from '../../interfaces/IAction';
import { IAnswer } from '../../interfaces/IQuizAnswer';
import { ModalHasSucced } from './components/ModalHasSucced';
import { ModalHasFailed } from './components/ModalHasFailed';
import { ModalNoMoreAttempts } from './components/ModalNoMoreAttempts';
import { ModalHasEmptyAnswers } from './components/ModalHasEmptyAnswers';

export function Quiz() {
  const { player, client } = useAuth();
  const navigate = useNavigate();

  const { actionId } = useParams();
  const [attempt, setAttempt] = useState<IAttempt>();
  const [chosenAnswer, setChosenAnswer] = useState<string>('');
  const [answers, setAnswers] = useState<IAnswer[]>([]);
  const [startQuiz, setStartQuiz] = useState<boolean>(false);
  const [finishedQuiz, setFinishedQuiz] = useState<boolean>(false);
  const [modalHasAnsweredIsOpen, setModalHasAnsweredIsOpen] =
    useState<boolean>(false);
  const [modalHasSucced, setModalHasSucced] = useState<boolean>(false);
  const [modalHasFailed, setModalHasFailed] = useState<boolean>(false);
  const [modalNoMoreAttempts, setModalNoMoreAttempts] =
    useState<boolean>(false);
  const [modalHasEmptyAnswers, setModalHasEmptyAnswers] =
    useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [seconds, setSeconds] = useState<number>(0);
  const [countDownVisible, setCountDownVisible] = useState<boolean>(false);
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const [accuracyPercentage, setAccuracyPercentage] = useState<number>(0);
  const [amountOfCorrectAnswers, setAmountOfCorrectAnswers] =
    useState<number>(0);
  const [quizPoints, setQuizPoints] = useState<number>(0);
  const [nOfAttemps, setNOfAttempts] = useState<number>(0);
  const [amountOfRetries, setAmountOfRetries] = useState<number>();

  useEffect(() => {
    startAnswer();
  }, []);

  useEffect(() => {
    if (countDownVisible) {
      const intervalId = setInterval(() => {
        setSeconds((tempoAnterior) => tempoAnterior - 1);
        if (seconds === 1) {
          handleFinish(answers, true);
        }
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [seconds]);

  const startAnswer = useCallback(async () => {
    const parameters = {
      playerId: player.id,
      actionId: actionId,
    };
    try {
      const { data } = await api.post('/actions/quiz/start', parameters);
      setAttempt(data.body);
      setStartQuiz(true);
      if (data.body.minutesTimer) {
        setCountDownVisible(true);
        setSeconds(data.body.minutesTimer * 60);
      }
      setNOfAttempts(data.body.nOfAttempts);
      setAmountOfRetries(data.body.amountOfRetries);
      const emptyAnswers = data.body.questions.map((question) => ({
        questionIndex: question.questionIndex,
        answer: '',
      }));
      setAnswers(emptyAnswers);
    } catch (error) {
      if (error.response.status === 403) setModalHasAnsweredIsOpen(true);
    }
  }, [actionId, player]);

  const sendAwsers = async (updatedAnswers: IAnswer[]) => {
    const parameters = {
      attemptId: attempt.attemptId,
      actionId: actionId,
      playerId: player.id,
      answers: updatedAnswers,
    };
    try {
      const { data } = await api.post('/actions/quiz/finish', parameters);
      setTotalPoints(data.body.totalPointsScored);
      setAccuracyPercentage(data.body.accuracyPercentage);
      setAmountOfCorrectAnswers(data.body.amountOfCorrectAnswers);
      setQuizPoints(data.body.quizRewardPoints);
      Notify(data.message, 'success');
      return data.body.totalPointsScored;
    } catch (error) {
      Notify(error.message, 'error');
    }
  };

  const handleSubmit = (): IAnswer[] => {
    const newAnswers = answers.map((question) => {
      return question.questionIndex === page
        ? { ...question, answer: chosenAnswer }
        : question;
    });
    setAnswers(newAnswers);
    return newAnswers;
  };

  const handleFinish = async (
    updatedAnswers: IAnswer[],
    finishedTime: boolean = false
  ) => {
    const hasEmptyAnswer = updatedAnswers.some((item) => item.answer === '');
    if (!hasEmptyAnswer || finishedTime) {
      const pointsEarned = await sendAwsers(updatedAnswers);
      setFinishedQuiz(true);
      if (pointsEarned > 0) {
        setModalHasSucced(true);
      }
      if (
        pointsEarned === 0 &&
        (!amountOfRetries || nOfAttemps < amountOfRetries)
      ) {
        setModalHasFailed(true);
      }
      if (pointsEarned === 0 && nOfAttemps === amountOfRetries) {
        setModalNoMoreAttempts(true);
      }
    } else {
      setModalHasEmptyAnswers(true);
    }
  };

  const formatarTempo = (tempoEmSegundos) => {
    const minutos = Math.floor(tempoEmSegundos / 60);
    const segundos = tempoEmSegundos % 60;
    return `${minutos.toString().padStart(2, '0')}:${segundos.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {!startQuiz && !finishedQuiz && (
        <div className="conteiner">
          <div className="container">
            <div className="row">
              <div className="my-3">
                <Skeleton height={50} />
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12 col-md-10">
                <Skeleton height={50} />
                <div className="mt-3">
                  <Skeleton height={400} />
                </div>
              </div>
              <div className="col-sm-12 col-md-2">
                <Skeleton height={200} />
              </div>
            </div>
          </div>
        </div>
      )}

      {startQuiz && !finishedQuiz && (
        <div className="container">
          <div className="row">
            <div className="mb-3">
              <ButtonBack title="Quiz" />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12 col-md-8">
              <div className="mb-3 mb-md-0">
                <div className="mb-3">
                  <Card>
                    <div className="">
                      <i className="material-icons-outlined float-start me-3 mt-1">
                        live_help
                      </i>
                      <p className="m-0 fs-4">
                        {attempt.questions[page].question}
                      </p>
                    </div>
                  </Card>
                </div>
                <Card>
                  <List>
                    <ListItem
                      cursorPointer={true}
                      onClick={() => setChosenAnswer('A')}
                      content={`${attempt.questions[page]?.alternatives.a}`}
                      secondaryContent={``}
                      withRange={true}
                      rangeChecked={chosenAnswer === 'A' ? true : false}
                    />
                    <ListItem
                      cursorPointer={true}
                      onClick={() => setChosenAnswer('B')}
                      content={`${attempt.questions[page]?.alternatives.b}`}
                      secondaryContent={``}
                      withRange={true}
                      rangeChecked={chosenAnswer === 'B' ? true : false}
                    />
                    <ListItem
                      cursorPointer={true}
                      onClick={() => setChosenAnswer('C')}
                      content={`${attempt.questions[page].alternatives.c}`}
                      secondaryContent={``}
                      withRange={true}
                      rangeChecked={chosenAnswer === 'C' ? true : false}
                    />
                    <ListItem
                      cursorPointer={true}
                      onClick={() => setChosenAnswer('D')}
                      content={`${attempt.questions[page]?.alternatives.d}`}
                      secondaryContent={``}
                      withRange={true}
                      rangeChecked={chosenAnswer === 'D' ? true : false}
                    />
                  </List>
                </Card>
              </div>
            </div>
            <div className="col-sm-12 col-md-4">
              <Card>
                <>
                  <p className="fw-semibold m-0">
                    Questões realizadas:{' '}
                    {answers.filter((item) => item.answer !== '').length} de{' '}
                    {answers.length}
                  </p>
                  <div
                    className="mt-3 justify-content-center"
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <div
                      style={{
                        borderRadius: '50%',
                        border: '1px solid black',
                        backgroundColor: `${client.primaryColor}`,
                        height: '10px',
                        width: '10px',
                        marginRight: '5px',
                      }}
                    />
                    <span style={{ paddingRight: '15px' }}>Respondido</span>
                    <div
                      style={{
                        borderRadius: '50%',
                        border: '1px solid black',
                        backgroundColor: `white`,
                        height: '10px',
                        width: '10px',
                        marginRight: '5px',
                      }}
                    />
                    <span style={{ paddingRight: '15px' }}>Sem Resposta</span>
                  </div>
                  <div
                    className="mt-3"
                    style={{ display: 'flex', gap: '10px' }}
                  >
                    {answers.map((answer) => (
                      <button
                        style={{
                          borderRadius: '50%',
                          border: '1px solid black',
                          backgroundColor: `${answer.answer !== '' || answer.questionIndex === page ? `${client.primaryColor}` : 'white'}`,
                          color: `${answer.answer !== '' || answer.questionIndex === page ? 'white' : 'black'}`,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '30px',
                          width: '30px',
                        }}
                        onClick={() => {
                          setPage(answer.questionIndex);
                          setChosenAnswer(answer.answer ? answer.answer : '');
                        }}
                      >
                        {answer.questionIndex + 1}
                      </button>
                    ))}
                  </div>
                  {countDownVisible && (
                    <>
                      <hr />
                      <div className="mb-3">
                        <p className="fw-semibold m-0">Tempo restante:</p>
                        <div className="mt-3 d-flex justify-content-center">
                          <i
                            className="material-icons-outlined"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '30px',
                            }}
                          >
                            schedule
                          </i>
                          <span
                            className={`fs-3 fw-semibold ${
                              seconds < 20 ? styles['blink-red'] : ''
                            }`}
                          >
                            {formatarTempo(seconds)}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </>
              </Card>
              <div style={{ marginTop: '10px' }}>
                <Button
                  style={{ width: '100%' }}
                  text={
                    page === attempt.questions?.length - 1 ||
                    answers.filter((item) => item.answer === '').length === 0
                      ? 'Finalizar'
                      : 'Continuar'
                  }
                  color="flat"
                  icon="east"
                  onClick={async () => {
                    const updatedAnswers = handleSubmit();
                    if (
                      page === attempt.questions?.length - 1 ||
                      answers.filter((item) => item.answer === '').length === 0
                    ) {
                      handleFinish(updatedAnswers);
                    } else {
                      setPage(page + 1);
                      setChosenAnswer(
                        answers[page + 1].answer ? answers[page + 1].answer : ''
                      );
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {finishedQuiz && (
        <div className="container">
          <div className="row">
            <div className="mb-3">
              <ButtonBack title="Quiz" />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12 col-md-8">
              <div className="mb-3 mb-md-0">
                <div className="mb-3">
                  <Card>
                    <div className="">
                      <i className="material-icons-outlined float-start me-3 mt-1">
                        live_help
                      </i>
                      <p className="m-0 fs-4">Resultado do Quiz</p>
                    </div>
                  </Card>
                </div>
                <Card>
                  <div className="d-flex flex-column align-items-center">
                    <div className="mb-3">
                      <div className={styles.circleIcon}>
                        <i className="material-icons-outlined">thumb_up</i>
                      </div>
                    </div>
                    <span className="fs-4 fw-semibold">
                      {accuracyPercentage}% de acertos
                    </span>
                  </div>
                  <div className="my-3">
                    <ProgressHorizontal value={accuracyPercentage} />
                  </div>
                  <div className="d-flex justify-content-center mt-4">
                    <Button
                      icon="check"
                      onClick={() => navigate(`${HOME}`)}
                      text="Finalizar"
                      color="flat"
                    />
                  </div>
                </Card>
              </div>
            </div>
            <div className="col-sm-12 col-md-4">
              <div className="mb-3">
                <Card>
                  <i className="material-icons-outlined float-start me-3 mt-1">
                    assignment_turned_in
                  </i>
                  <p className="m-0 fs-4">Pontuação</p>
                  <div className="mt-3">
                    <p>Este quiz vale:</p>
                    <span
                      className={`badge bg-secondary w-100 center-text color-text fs-5 border bg-transparent`}
                    >
                      {`${quizPoints} ${client?.coinName ? client.coinName.replace(/\((\w+)\)/, (_, conteudo) => conteudo) : 'pontos'}`}
                    </span>
                  </div>
                  <div className="mt-3">
                    <p>Sua pontuação foi:</p>
                    <span
                      className={`badge bg-secondary w-100 center-text fs-5 ${
                        totalPoints > 0 ? 'bg-success' : 'bg-danger'
                      }`}
                    >
                      {`${totalPoints} ${client?.coinName ? client.coinName.replace(/\((\w+)\)/, (_, conteudo) => conteudo) : 'pontos'}`}
                    </span>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      )}

      <ModalHasAnswered
        modalIsOpen={modalHasAnsweredIsOpen}
        toggleModal={() => setModalHasAnsweredIsOpen(false)}
        cancelAnswer={() => navigate(`${ACTION_DETAILS}/${actionId}`)}
      />
      <ModalHasSucced
        modalIsOpen={modalHasSucced}
        toggleModal={() => setModalHasSucced(false)}
        earnedPoints={totalPoints}
        correctAnswers={amountOfCorrectAnswers}
      />
      <ModalHasFailed
        modalIsOpen={modalHasFailed}
        toggleModal={() => setModalHasFailed(false)}
        cancelAnswer={() => navigate(`${HOME}`)}
        tryAgain={() => navigate(`${ACTION_DETAILS}/${actionId}`)}
      />
      <ModalNoMoreAttempts
        modalIsOpen={modalNoMoreAttempts}
        toggleModal={() => setModalNoMoreAttempts(false)}
      />
      <ModalHasEmptyAnswers
        modalIsOpen={modalHasEmptyAnswers}
        toggleModal={() => setModalHasEmptyAnswers(false)}
        finishQuiz={async () => {
          await sendAwsers(answers);
          setFinishedQuiz(true);
        }}
      />
    </>
  );
}
